import { Switch, Route, useLocation } from "wouter";
import { useEffect } from "react";
import { ErrorBoundary } from "@/components/error-boundary";
import { queryClient, apiRequest } from "./lib/queryClient";
import { observeImpersonationReadOnly } from "./lib/impersonationReadOnly";
import { toast } from "@/hooks/use-toast";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

function usePageTracking() {
  const [location] = useLocation();

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location,
        page_location: window.location.href,
      });
    }
  }, [location]);
}
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import DashboardHeader from "@/components/DashboardHeader";
import { UserProvider } from "@/contexts/UserContext";
import { SocketProvider } from "@/contexts/SocketContext";
import { useAuth } from "@/hooks/useAuth";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SelectRole from "@/pages/SelectRole";
import SelectMembership from "@/pages/SelectMembership";
import SignupShipper from "@/pages/SignupShipper";
import SignupCarrier from "@/pages/SignupCarrier";
import SignupBroker from "@/pages/SignupBroker";
import SignupSuccess from "@/pages/SignupSuccess";
import SignupWelcome from "@/pages/SignupWelcome";
import Dashboard from "@/pages/Dashboard";
import PostLoad from "@/pages/PostLoad";
import PostTruck from "@/pages/PostTruck";
import FindTrucks from "@/pages/FindTrucks";
import FindLoads from "@/pages/FindLoads";
import Messages from "@/pages/Messages";
import Membership from "@/pages/Membership";
import Settings from "@/pages/Settings";
import EditProfile from "@/pages/EditProfile";
import Profile from "@/pages/Profile";
import ProfilePage from "@/pages/ProfilePage";
import NotFound from "@/pages/NotFound";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminVerification from "@/pages/AdminVerification";
import AdminUsers from "@/pages/AdminUsers";
import AdminLoads from "@/pages/AdminLoads";
import AdminLoadBoard from "@/pages/AdminLoadBoard";
import AdminTrucks from "@/pages/AdminTrucks";
import AdminContactSubmissions from "@/pages/AdminContactSubmissions";
import AdminSettings from "@/pages/AdminSettings";
import AdminAccessManagement from "@/pages/AdminAccessManagement";
import AdminMessages from "@/pages/AdminMessages";
import { AdminLayout } from "@/components/AdminLayout";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import Support from "@/pages/Support";
import PreferencesSettings from "@/pages/PreferencesSettings";
import ExternalParties from "@/pages/ExternalParties";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvite from "@/pages/AcceptInvite";
import Feedback from "@/pages/Feedback";
import CompletePaymentSetup from "@/pages/CompletePaymentSetup";
import FeedbackButton from "@/components/FeedbackButton";
import History from "@/pages/History";
import BidComparison from "@/pages/BidComparison";
import AdminLogin from "@/pages/AdminLogin";
import PreferredPartners from "@/pages/PreferredPartners";
import ReferralPage from "@/pages/ReferralPage";
import Upgrade from "@/pages/Upgrade";
import JoinPage from "@/pages/JoinPage";
import { EmailVerificationBanner } from "@/components/VerificationBanner";
import { PaymentProblemBanner } from "@/components/PaymentProblemBanner";

// Redirect component that uses useEffect to avoid loops
function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation(to);
  }, [to, setLocation]);
  return null;
}

function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user } = useAuth();
  const showEmailBanner = location !== "/verify-email" && !!user && user.emailVerified === false;
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  const { data: impersonationStatus } = useQuery<{
    isImpersonating: boolean;
    targetUser: { firstName: string; email: string } | null;
  }>({
    queryKey: ["/api/admin/impersonation-status"],
    refetchInterval: 30000,
  });

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-[100dvh] w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden min-h-0">
          <DashboardHeader />
          {showEmailBanner && (
            <div className="px-6 pt-3 flex-shrink-0">
              <EmailVerificationBanner />
            </div>
          )}
          {impersonationStatus?.isImpersonating && (
            <div className="bg-orange-500 text-white px-4 py-2 text-sm font-medium flex items-center justify-between">
              <span>Viewing as {impersonationStatus.targetUser?.firstName || impersonationStatus.targetUser?.email} — read-only. Changes are turned off.</span>
              <button
                onClick={async () => {
                  await apiRequest("POST", "/api/admin/impersonate/end");
                  queryClient.cancelQueries();
                  queryClient.clear();
                  // Wait for admin session to be recognized before navigating
                  await queryClient.invalidateQueries({ queryKey: ["/api/admin/auth/me"] });
                  await queryClient.refetchQueries({ queryKey: ["/api/admin/auth/me"] });
                  window.location.href = "/admin/users";
                }}
                className="underline font-bold ml-4"
              >
                Exit — Return to Admin
              </button>
            </div>
          )}
          <PaymentProblemBanner />
          <main className="flex-1 overflow-auto">
            {children}
          </main>
          <FeedbackButton />
        </div>
      </div>
    </SidebarProvider>
  );
}

function Router() {
  usePageTracking();
  useCsrfToken();
  const { user, isAuthenticated, isLoading } = useAuth();
  const profileCompleted = user?.profileCompleted === "true" || (user?.profileCompleted as unknown) === true;
  const isAdmin = user?.isAdmin === "true" || (user?.isAdmin as unknown) === true;
  const paymentSetupComplete = user?.paymentSetupComplete === "true" || (user?.paymentSetupComplete as unknown) === true;
  const isTestAccount = user?.isTestAccount === true || (user?.isTestAccount as unknown) === "true";
  const needsPaymentSetup = isAuthenticated && profileCompleted && !paymentSetupComplete && !isAdmin && !isTestAccount;

  const { data: adminUser, isLoading: isAdminLoading } = useQuery({
    queryKey: ["/api/admin/auth/me"],
    retry: false,
    staleTime: 30000,
  });

  const isAdminAuthenticated = isAdminLoading || isAdmin || (!!adminUser && !(adminUser as any).mustChangePassword);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Public routes - always accessible
  return (
    <Switch>
      {/* Public routes */}
      <Route path="/" component={Home} />
      <Route path="/how-it-works" component={HowItWorks} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-use" component={TermsOfUse} />
      <Route path="/support" component={Support} />
      <Route path="/feedback" component={Feedback} />
      <Route path="/join/:code" component={JoinPage} />

      {/* Admin login route */}
      <Route path="/admin-login" component={AdminLogin} />
      
      {/* Auth routes */}
      <Route path="/login">
        {isAuthenticated ? (
          isAdmin ? <Redirect to="/admin" /> :
          profileCompleted ? <Redirect to="/dashboard" /> :
          <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} />
        ) : <Login />}
      </Route>
      <Route path="/register">
        {isAuthenticated ? (() => {
          const params = new URLSearchParams(window.location.search);
          const role = params.get("role");
          if (role && ["shipper", "carrier", "broker"].includes(role)) {
            return <Redirect to={`/select-membership/${role}`} />;
          }
          return <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} />;
        })() : <Register />}
      </Route>
      
      <Route path="/forgot-password">
        {isAuthenticated ? <Redirect to="/dashboard" /> : <ForgotPassword />}
      </Route>
      <Route path="/reset-password">
        {isAuthenticated ? <Redirect to="/dashboard" /> : <ResetPassword />}
      </Route>
      <Route path="/verify-email" component={VerifyEmail} />
      <Route path="/accept-invite" component={AcceptInvite} />
      
      {/* Signup flow routes */}
      <Route path="/select-role">
        {isAuthenticated
          ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} />
          : <Redirect to="/register" />}
      </Route>
      <Route path="/select-membership/:role">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         profileCompleted ? <Redirect to="/dashboard" /> : <SelectMembership />}
      </Route>
      <Route path="/signup/shipper">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         profileCompleted ? <Redirect to="/dashboard" /> : <SignupShipper />}
      </Route>
      <Route path="/signup/carrier">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         profileCompleted ? <Redirect to="/dashboard" /> : <SignupCarrier />}
      </Route>
      <Route path="/signup/broker">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         profileCompleted ? <Redirect to="/dashboard" /> : <SignupBroker />}
      </Route>
      <Route path="/signup-success" component={SignupSuccess} />
      <Route path="/signup-welcome">
        {!isAuthenticated ? <Redirect to="/login" /> :
         user?.userType !== "Shipper" ? <Redirect to="/dashboard" /> :
         <SignupWelcome />}
      </Route>
      <Route path="/complete-payment-setup">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> :
         paymentSetupComplete ? <Redirect to="/dashboard" /> : <CompletePaymentSetup />}
      </Route>
      
      {/* Admin routes */}
      <Route path="/admin">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminDashboard /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/verification">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminVerification /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/users">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminUsers /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/loads">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminLoads /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/load-board">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminLoadBoard /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/trucks">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminTrucks /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/contact-submissions">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminContactSubmissions /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/settings">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminSettings /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/admin-access">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminAccessManagement /></AdminLayout>
        )}
      </Route>
      <Route path="/admin/messages">
        {!isAdminAuthenticated ? <Redirect to="/admin-login" /> : (
          <AdminLayout><AdminMessages /></AdminLayout>
        )}
      </Route>
      
      {/* Dashboard routes - require authentication, completed profile, and payment setup */}
      <Route path="/loads/:loadId/bids">
        {!isAuthenticated ? <Redirect to="/login" /> :
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> :
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><BidComparison /></DashboardLayout>
        )}
      </Route>
      <Route path="/dashboard">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><Dashboard /></DashboardLayout>
        )}
      </Route>
      <Route path="/history">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><History /></DashboardLayout>
        )}
      </Route>
      <Route path="/post-load">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><PostLoad /></DashboardLayout>
        )}
      </Route>
      <Route path="/post-truck">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><PostTruck /></DashboardLayout>
        )}
      </Route>
      <Route path="/find-trucks">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : 
         user?.userType === "Carrier" ? <Redirect to="/find-loads" /> : (
          <DashboardLayout><FindTrucks /></DashboardLayout>
        )}
      </Route>
      <Route path="/find-loads">
        {!isAuthenticated ? <Redirect to={`/login?returnTo=${encodeURIComponent('/find-loads' + window.location.search)}`} /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><FindLoads /></DashboardLayout>
        )}
      </Route>
      <Route path="/messages">
        <Redirect to="/shipments" />
      </Route>
      <Route path="/shipments">
        {!isAuthenticated ? <Redirect to={`/login?returnTo=${encodeURIComponent('/shipments' + window.location.search)}`} /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><Messages /></DashboardLayout>
        )}
      </Route>
      <Route path="/membership">
        {() => { window.location.replace("/settings"); return null; }}
      </Route>
      <Route path="/settings">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><Settings /></DashboardLayout>
        )}
      </Route>
      <Route path="/edit-profile">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><EditProfile /></DashboardLayout>
        )}
      </Route>
      <Route path="/profile">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><Profile /></DashboardLayout>
        )}
      </Route>
      <Route path="/preferences">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><PreferencesSettings /></DashboardLayout>
        )}
      </Route>
      <Route path="/external-contacts">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><ExternalParties /></DashboardLayout>
        )}
      </Route>
      <Route path="/preferred-partners">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><PreferredPartners /></DashboardLayout>
        )}
      </Route>
      <Route path="/referral">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><ReferralPage /></DashboardLayout>
        )}
      </Route>
      <Route path="/upgrade">
        {!isAuthenticated ? <Redirect to="/login" /> :
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> :
         <DashboardLayout><Upgrade /></DashboardLayout>}
      </Route>
      <Route path="/profile/:userId">
        {!isAuthenticated ? <Redirect to="/login" /> : 
         !profileCompleted ? <Redirect to={`/select-membership/${(user?.userType ?? "shipper").toLowerCase()}`} /> : 
         needsPaymentSetup ? <Redirect to="/complete-payment-setup" /> : (
          <DashboardLayout><ProfilePage /></DashboardLayout>
        )}
      </Route>
      
      {/* 404 fallback */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  useEffect(() => observeImpersonationReadOnly(window, (message) => {
    // Show after route-local catch handlers so a generic error toast cannot
    // immediately replace the server's specific read-only explanation.
    window.setTimeout(() => {
      toast({ title: "Read-only view", description: message, variant: "destructive" });
    }, 0);
  }), []);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <UserProvider>
            <SocketProvider>
              <Toaster />
              <Router />
            </SocketProvider>
          </UserProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
